import { useState, useCallback } from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Text,
  Tr,
  Td,
  Skeleton,
  Image,
  Tooltip
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faBooks,
  faArrowRight
} from '@fortawesome/pro-regular-svg-icons';
import { Button, ButtonIcon, Link, SearchInput } from '@morressier/alembic';
import { Helmet } from 'react-helmet';
import { fetchAuthRealmJournals } from '../../api/queries';
import Footer from '../../components/Footer';
import { clientAuthRealm } from '../../utils/env';
import { useNavigate } from 'react-router-dom';
import { ISubmissionContextListView } from '@publishing-engine/domain/views';

// Constants
const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 100;

const JournalIndex = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [searchParams, setSearchParams] = useState('');

  const { data: journals, isLoading } = useQuery<ISubmissionContextListView>({
    queryKey: ['auth-realm-journals', { clientAuthRealm, searchParams }],
    queryFn: () =>
      fetchAuthRealmJournals(
        clientAuthRealm ?? '',
        DEFAULT_PAGE,
        DEFAULT_PAGE_SIZE,
        searchParams ?? ''
      ),
    enabled: !!clientAuthRealm
  });

  const totalJournals = journals?.total ?? 0;
  const websiteUrl = journals?.websiteUrl;

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setSearchParams(searchValue.trim());
      }
    },
    [searchValue]
  );

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
    setSearchParams('');
  }, []);

  const handleJournalClick = useCallback(
    (journalId: string) => {
      navigate(`/j/${journalId}`);
    },
    [navigate]
  );

  return (
    <Box id="scrollable-wrapper" overflowY="auto" height="100%">
      <Helmet>
        <title>Start Your Submission</title>
      </Helmet>
      <Box height="100%">
        <Box padding={{ base: 3, md: 5 }} minHeight="100%">
          <Flex flexDirection="column" width="100%" gap={4}>
            {/* Header Section */}
            <Box mb={{ base: 3, md: 5 }}>
              <Text textStyle="headingSection" mb={2}>
                Start your Submission
              </Text>
              <Text textStyle="textBody">
                Select a journal below to begin the manuscript submission
                process.
                {websiteUrl && (
                  <>
                    {' '}
                    For a clearer understanding of the submission process, visit
                    the{' '}
                    <Link variant="secondary" href={websiteUrl} target="_blank">
                      publisher's website
                    </Link>
                    .
                  </>
                )}
              </Text>
            </Box>

            {/* Search Bar Section */}
            <Flex
              gap={3}
              justifyContent="space-between"
              alignItems={{ base: 'flex-start', md: 'center' }}
              direction={{ base: 'column', md: 'row' }}
            >
              <Text textStyle="headingGroup">Journals ({totalJournals})</Text>
              <Flex gap={2} alignItems="center">
                <SearchInput
                  placeholder="Search journal by title/short name"
                  iconLeft={<FontAwesomeIcon icon={faSearch} />}
                  width={{ base: '100%', md: '400px' }}
                  name="searchParams"
                  value={searchValue}
                  enterKeyHint="search"
                  onChange={e => setSearchValue(e.currentTarget.value)}
                  onInputClear={handleSearchClear}
                  onKeyDown={handleSearchKeyDown}
                  onKeyUp={handleSearchKeyDown}
                />
                <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
                  <Button
                    variant="primary"
                    aria-label="Search"
                    onClick={() => setSearchParams(searchValue.trim())}
                  >
                    <FontAwesomeIcon icon={faSearch} />
                  </Button>
                </Box>
              </Flex>
            </Flex>

            {/* Journals Table Section */}
            <Box width="100%">
              {isLoading ? (
                <Box
                  border="0.5px solid #D0D5DD"
                  borderRadius="4px"
                  backgroundColor="#ffffff"
                  padding={4}
                >
                  <Flex flexDirection="column" gap={4}>
                    {[1, 2, 3].map(index => (
                      <Flex key={index} gap={4} alignItems="center">
                        <Skeleton height="50px" width="100%" />
                      </Flex>
                    ))}
                  </Flex>
                </Box>
              ) : journals?.collection && journals.collection.length > 0 ? (
                <>
                  <Box
                    display={{ base: 'none', md: 'block' }}
                    backgroundColor="#ffffff"
                    overflowY="auto"
                    width="100%"
                  >
                    <Table width="100%">
                      <Tbody>
                        {journals.collection.map(journal => (
                          <Tr
                            key={journal.id}
                            _hover={{ bg: '#F7FAFC' }}
                            cursor="pointer"
                            onClick={() => handleJournalClick(journal.id)}
                            sx={{
                              '& td': {
                                borderBottom: '1px solid',
                                borderTop: '1px solid',
                                borderColor: '#D0D5DD',
                                py: 4
                              }
                            }}
                          >
                            <Td width="10%">
                              <Flex
                                width="125px"
                                height="50px"
                                borderRadius="md"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Image
                                  src={journal.config?.logoUrl}
                                  alt={`${
                                    journal.data?.name ?? 'Journal'
                                  } logo`}
                                  width="100%"
                                  height="100%"
                                  objectFit="contain"
                                />
                              </Flex>
                            </Td>
                            <Td width="50%">
                              <Text textStyle="textBody">
                                {journal.data?.name}
                              </Text>
                            </Td>
                            <Td>
                              <Text textStyle="textBody">
                                ISSN: {journal.config?.issn?.online ?? 'N/A'}
                              </Text>
                            </Td>
                            <Td textAlign="right">
                              <Tooltip
                                hasArrow
                                shouldWrapChildren
                                placement="top"
                                label="Start submission"
                                borderRadius={4}
                              >
                                <ButtonIcon
                                  size="regular"
                                  ariaLabel="Go to  create Submission"
                                  icon={<FontAwesomeIcon icon={faArrowRight} />}
                                  data-cy="info-icon"
                                  onClick={() => handleJournalClick(journal.id)}
                                />
                              </Tooltip>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  <Flex
                    display={{ base: 'flex', md: 'none' }}
                    direction="column"
                    gap={3}
                    width="100%"
                  >
                    {journals.collection.map(journal => (
                      <Box
                        key={journal.id}
                        border="1px solid #D0D5DD"
                        borderRadius="8px"
                        backgroundColor="#ffffff"
                        padding={3}
                      >
                        <Flex direction="column" gap={3}>
                          <Flex
                            width="100px"
                            height="40px"
                            borderRadius="md"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Image
                              src={journal.config?.logoUrl}
                              alt={`${journal.data?.name ?? 'Journal'} logo`}
                              width="100%"
                              height="100%"
                              objectFit="contain"
                            />
                          </Flex>
                          <Text textStyle="textBody">{journal.data?.name}</Text>
                          <Text textStyle="textBody">
                            ISSN: {journal.config?.issn?.online ?? 'N/A'}
                          </Text>
                          <Flex justifyContent="flex-end">
                            <ButtonIcon
                              size="regular"
                              ariaLabel="Go to  create Submission"
                              icon={<FontAwesomeIcon icon={faArrowRight} />}
                              data-cy="info-icon"
                              onClick={() => handleJournalClick(journal.id)}
                            />
                          </Flex>
                        </Flex>
                      </Box>
                    ))}
                  </Flex>
                </>
              ) : (
                <Box
                  border="0.5px solid #D0D5DD"
                  borderRadius="4px"
                  backgroundColor="#ffffff"
                  padding="80px 24px"
                >
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={3}
                  >
                    <FontAwesomeIcon icon={faBooks} fontSize={32} />
                    <Text textStyle="headingSection">No Journals Found</Text>
                    <Text textStyle="textBody" textAlign="center">
                      {searchParams
                        ? 'No journals found matching your search criteria. Try adjusting your search terms.'
                        : 'There are currently no journals available.'}
                    </Text>
                  </Flex>
                </Box>
              )}
            </Box>
          </Flex>
        </Box>

        <Footer />
      </Box>
    </Box>
  );
};

export default JournalIndex;
